import styled from "styled-components";
import { formatMoney } from "../../../utils/formatMoney";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import {
  addMultiCurrencyMoney,
  zeroMultiCurrencyMoney,
  multiplyMultiCurrencyMoney,
} from "@KassaroAB/shared/utils/multiCurrencyMoney";
import type { MultiCurrencyMoney } from "@KassaroAB/shared/types/multiCurrencyMoney";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import type { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import { mapBillingPeriodToMonths } from "@KassaroAB/shared/utils/billingPeriod";
import { useTranslation } from "react-i18next";
import type { RestAvailableBillingItem } from "@KassaroAB/shared/types/availableBillingItem";

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${spacing.x3}px;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const SummaryLabel = styled.span`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const SummaryValue = styled.span`
  font-size: ${fontSizes.large}px;
  font-weight: 600;
  color: ${colors.text};
`;

const formatSummaryMoney = (money: MultiCurrencyMoney): string => {
  const sek = money.currencies?.[Currency.SEK];
  if (!sek || sek.amount === 0) return "–";
  return formatMoney(sek);
};

const getItemPerPeriodCost = (
  item: RestAvailableBillingItem,
  paymentTerms: BillingPeriod
): MultiCurrencyMoney => {
  const { monthlyPrice, prepaid36MonthPrice } = item.pricing || {};
  if (!monthlyPrice && !prepaid36MonthPrice) return zeroMultiCurrencyMoney();

  const months = mapBillingPeriodToMonths(paymentTerms);

  if (months >= 36 && prepaid36MonthPrice) {
    return multiplyMultiCurrencyMoney(prepaid36MonthPrice, months);
  }

  if (monthlyPrice) {
    return multiplyMultiCurrencyMoney(monthlyPrice, months);
  }

  return zeroMultiCurrencyMoney();
};

const getItemPerMonthCost = (
  item: RestAvailableBillingItem,
  paymentTerms: BillingPeriod
): MultiCurrencyMoney => {
  const { monthlyPrice, prepaid36MonthPrice } = item.pricing || {};
  const months = mapBillingPeriodToMonths(paymentTerms);
  if (months >= 36 && prepaid36MonthPrice) {
    return prepaid36MonthPrice;
  }

  if (monthlyPrice) {
    return monthlyPrice;
  }

  return zeroMultiCurrencyMoney();
};

const getPerPeriodCost = (
  billingItems: RestAvailableBillingItem[],
  paymentTerms: BillingPeriod
): MultiCurrencyMoney =>
  billingItems.reduce(
    (sum, item) => addMultiCurrencyMoney(sum, getItemPerPeriodCost(item, paymentTerms)),
    zeroMultiCurrencyMoney()
  );

const getPerMonthCost = (
  billingItems: RestAvailableBillingItem[],
  paymentTerms: BillingPeriod
): MultiCurrencyMoney =>
  billingItems.reduce(
    (sum, item) => addMultiCurrencyMoney(sum, getItemPerMonthCost(item, paymentTerms)),
    zeroMultiCurrencyMoney()
  );

const getPaidImmediatelyCost = (
  firstPeriodCost: MultiCurrencyMoney,
  billingItems: RestAvailableBillingItem[]
): MultiCurrencyMoney =>
  billingItems.reduce(
    (sum, item) =>
      item.pricing?.oneTimePrice
        ? addMultiCurrencyMoney(sum, item.pricing.oneTimePrice)
        : sum,
    addMultiCurrencyMoney(zeroMultiCurrencyMoney(), firstPeriodCost)
  );

interface PriceSummarySectionProps {
  billingItems: RestAvailableBillingItem[];
  paymentTerms: BillingPeriod;
  quantity?: number;
}

export const PriceSummarySection = ({
  billingItems,
  paymentTerms,
  quantity = 1,
}: PriceSummarySectionProps) => {
  const { t } = useTranslation();
  const perPeriodCost = multiplyMultiCurrencyMoney(
    getPerPeriodCost(billingItems, paymentTerms),
    quantity
  );
  const perMonthCost = multiplyMultiCurrencyMoney(
    getPerMonthCost(billingItems, paymentTerms),
    quantity
  );
  const paidImmediatelyCost = multiplyMultiCurrencyMoney(
    getPaidImmediatelyCost(
      getPerPeriodCost(billingItems, paymentTerms),
      billingItems
    ),
    quantity
  );

  return (
    <SummaryGrid>
      <SummaryItem>
        <SummaryLabel>{t("packageSelection.perPeriod")}</SummaryLabel>
        <SummaryValue>{formatSummaryMoney(perPeriodCost)}</SummaryValue>
      </SummaryItem>
      <SummaryItem>
        <SummaryLabel>{t("packageSelection.perMonthUnit")}</SummaryLabel>
        <SummaryValue>{formatSummaryMoney(perMonthCost)}</SummaryValue>
      </SummaryItem>
      <SummaryItem>
        <SummaryLabel>{t("packageSelection.paidImmediately")}</SummaryLabel>
        <SummaryValue>{formatSummaryMoney(paidImmediatelyCost)}</SummaryValue>
      </SummaryItem>
    </SummaryGrid>
  );
};
